import React from "react";
//#region redux
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { stoppedPaymentsReportUpdated } from "store/actions/stoppedPaymentsActions";
//#endregion
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { Amount } from "models/utilTypes/currencyType";
import { CurrencyEnums } from "models/enums";
import { Supplier, User } from "models/propTypes/userProfilePropTypes";
import { StoppedPaymentListTableProps } from "models/propTypes/paymentPropTypes";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import PaymentList from "../paymentListTable";
import { useIncomingInvoiceService } from "serviceHooks";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";

const StoppedPaymentsList: React.FC<StoppedPaymentListTableProps> = (props) => {
  const incomingInvoiceService = useIncomingInvoiceService();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const [loadingRows, setLoadingRows] = React.useState<WintSuperkollAPIModel.StoppedPayment[]>([]);
  React.useEffect(() => {
    // eslint-disable-next-line eqeqeq --- since checked against different types
    const currentLoadingRows = loadingRows.filter((r) => props.items?.some((i) => i.id == r.id));
    setLoadingRows(currentLoadingRows);
  }, [props.items]);

  const onAlreadyPaidClick = async (row: WintSuperkollAPIModel.StoppedPayment) => {
    setLoadingRows([...loadingRows, row]);
    incomingInvoiceService.markAsAlreadyPaid(row.id).then((res) => {
      props.reloadHandler && props.reloadHandler((result) => dispatch(stoppedPaymentsReportUpdated(result)));
    });
  };

  const onRowClick = (row: WintSuperkollAPIModel.StoppedPayment) => {
    if (row.type === WintSuperkollAPIModel.StoppedPaymentType.IncomingInvoice) {
      navigate(UrlHelper.formatPath(`/IncomingInvoice/${row.id}`), {
        state: { background: location },
      });
    } else {
      navigate(UrlHelper.formatPath(`/Receipt/${row.id}`), {
        state: { background: location },
      });
    }
  };

  const recipientProfileFormatter = (stoppedPayment: WintSuperkollAPIModel.StoppedPayment) => {
    if (stoppedPayment.type === WintSuperkollAPIModel.StoppedPaymentType.IncomingInvoice) {
      return new Supplier(stoppedPayment.recipientProfileId, stoppedPayment.recipientName);
    } else {
      return new User(stoppedPayment.recipientProfileId, stoppedPayment.recipientName);
    }
  };

  return (
    <PaymentList
      properties={{
        recipientProfileId: {
          description: TranslationKeys.Common.Counterpart,
          formatter: recipientProfileFormatter,
          noWrap: true,
          disableSort: true,
          className: cssExports.textBlue,
        },
        paymentFailDate: {
          description: TranslationKeys.Payments.PaymentStopped,
          formatter: (stoppedPayment: WintSuperkollAPIModel.StoppedPayment) =>
            stoppedPayment.paymentFailDate && new Date(stoppedPayment.paymentFailDate),
          disableSort: true,
          noWrap: true,
        },
        dueDate: {
          description: TranslationKeys.Common.Due_Date,
          formatter: (stoppedPayment: WintSuperkollAPIModel.StoppedPayment) =>
            stoppedPayment.dueDate && new Date(stoppedPayment.dueDate),
          disableSort: true,
          noWrap: true,
        },
        amount: {
          description: TranslationKeys.Common.Amount,
          formatter: (stoppedPayment: WintSuperkollAPIModel.StoppedPayment) =>
            new Amount(stoppedPayment.currency ?? CurrencyEnums.CurrencyCode.SEK, stoppedPayment.amount),
          disableSort: true,
          alignment: "right",
          className: cssExports.textBlack,
          noWrap: true,
        },
        group: {
          formatter: (stoppedPayment: WintSuperkollAPIModel.StoppedPayment) => {
            if (stoppedPayment.type === WintSuperkollAPIModel.StoppedPaymentType.IncomingInvoice)
              return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.IncomingInvoice.IncomingInvoices);
            else return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Receipts);
          },
        },
      }}
      onRowSelectionsChange={props.onRowSelectionsChange}
      items={props.items?.map((i) => ({
        ...i,
        select: props.selectedPayments.some((s) => s.id === i.id && s.type === i.type),
        loading: loadingRows.some((lr) => lr.id === i.id && lr?.type === i.type),
      }))}
      moreActions={(stoppedPayment: WintSuperkollAPIModel.StoppedPayment) =>
        stoppedPayment.type === WintSuperkollAPIModel.StoppedPaymentType.IncomingInvoice
          ? [
              {
                icon: IconDefinitionConstants.WCheck,
                description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.AlreadyPaid),
                onClickHandler: () => onAlreadyPaidClick(stoppedPayment),
              },
            ]
          : []
      }
      onRowClick={onRowClick}
    ></PaymentList>
  );
};

export default StoppedPaymentsList;
